import React, { createRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import {
  userLogin,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_REASON_UNKNOWN,
  setOperationStatus,
} from '@uncinc/react-drupal-core';

import {
  Container,
  FormikWrapper,
  FormEmail as EmailField,
} from '@uncinc/uncinc-react-kitchen-sink';

import { Button, PasswordField, Tooltip, Icon, Main } from '../../components';
import {
  DOMAIN_ZELFHULP_ALCOHOL,
  ENABLE_AIDA_SCREENING,
  FEATURE_FLAGS,
  HTI_SSO_NAME,
  IS_REACT_NATIVE,
  URL_JELLINEK_ZELFHULP,
} from '../../config/constants';
import getCurrentDomain from '../../helpers/getCurrentDomain';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import useOperationStatus from '../../hooks/useOperationStatus';
import useToast from '../../hooks/useToast';
import { userLoginWithSso } from '../../store/actions/Login';
import { showNativeAppSelector } from '../../store/actions/UI';

import './style.scss';

const PageLogin = () => {
  const ref = createRef();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('PageLogin');
  const { onboarding, resetPasswordRequest } = useRoutes();
  const toast = useToast('error');

  const loginOperationStatus = useOperationStatus('user', 'login');
  const siteName = useSelector((state) => state.getIn(['uiInternal', 'currentDomain', 'pretty'], null));

  useEffect(() => {
    if (loginOperationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.login.incorrect_credentials'));
    }

    if (loginOperationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'login', null, UI_OPERATION_STATUS_REASON_UNKNOWN));
    }
  }, [loginOperationStatus, t, toast, dispatch]);

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('pages.login.fields.email.validation.email'))
      .required(t('pages.login.fields.email.validation.required')),
    password: Yup.string().required(t('pages.login.fields.password.validation.required')),
  });

  /**
   * Send out dispatch to return to the native app selector
   * Only used in react native
   */
  const onChangeProgramClicked = (event) => {
    if (IS_REACT_NATIVE) {
      event.stopPropagation();
      dispatch(showNativeAppSelector());
    }
  };

  return (
    <Page className={buildClass()} hideMenuMain title={t('pages.login.documentTitle')}>
      <Container>
        <div className="row">
          <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-12">
            <div className={buildClass('title')}>
              <h1>{t('pages.login.title', { siteName })}</h1>
            </div>

            <FormikWrapper
              initialValues={{ email: '', password: '' }}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={({ email, password }, { setSubmitting }) => {
                dispatch(userLogin(email, password));
                setSubmitting(false);
              }}
            >
              <EmailField
                fieldName="email"
                title={t('pages.login.fields.email.label')}
                placeholder={t('pages.login.fields.email.placeholder')}
              />
              <div className={buildClass('password-field-wrapper')}>
                <PasswordField
                  fieldName="password"
                  title={t('pages.login.fields.password.label')}
                />
                <Link to={resetPasswordRequest} className="textual-link">
                  {t('pages.login.fields.password.forgot_password')}
                </Link>
              </div>
              <Button
                htmlType="submit"
                className={buildClass('submit')}
                buttonState={loginOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
              >
                {t('pages.login.submit')}
              </Button>
            </FormikWrapper>

            <Link
              role="button"
              className="Button Button--secondary"
              to={{
                pathname: ENABLE_AIDA_SCREENING && getCurrentDomain() === DOMAIN_ZELFHULP_ALCOHOL
                  ? onboarding.credentials
                  : onboarding.checklist,
                state: pathname,
              }}
            >
              {t('pages.login.register')}
            </Link>

            {FEATURE_FLAGS.zelfhulp_hti && !IS_REACT_NATIVE && (
              <div className={buildClass('hti')}>
                <Link
                  role="button"
                  className="Button mt-2"
                  onClick={() => {
                    dispatch(userLoginWithSso());
                  }}
                >
                  {t('pages.login.sso', { sso_name: HTI_SSO_NAME })}
                </Link>
                <div className={buildClass('hti__info')} ref={ref}>
                  <Icon icon="info" variant="7" />
                  <Tooltip targetRef={ref}>
                    {t('pages.login.sso_tooltip', { sso_name: HTI_SSO_NAME })}
                  </Tooltip>
                </div>
              </div>
            )}
            <a href={IS_REACT_NATIVE ? '' : URL_JELLINEK_ZELFHULP} onClick={onChangeProgramClicked} className={joinClasses(buildClass('program-link'), 'textual-link')}>
              {t('pages.login.change_program')}
            </a>

          </Main>
        </div>
      </Container>
    </Page>
  );
};

export default PageLogin;
